import { Component, effect, ElementRef, OnInit, output, signal, viewChild } from '@angular/core';

@Component({
  selector: 'lib-basys-graphics-core-scalable-image',
  imports: [],
  templateUrl: './scalable-image.component.html',
  styleUrl: './scalable-image.component.scss',
})
export class ScalableImageComponent<
  T extends { left: number; right: number; top: number; bottom: number; points?: { x: number; y: number }[] },
> implements OnInit
{
  private readonly _ZoomIncrementValue = 1.1;
  private readonly _ZoomDecrementValue = 0.9;

  image = signal<{ imageBase64: string; width: number; height: number; initialMarking?: T }>({
    imageBase64: '',
    width: 0,
    height: 0,
  });
  clickableFeatures = signal<T[]>([]);
  onFeatureClicked = output<T | undefined>();
  size = signal<{ width: number; height: number; resetImage?: boolean }>({ width: 0, height: 0 });

  get zoomLevel(): number {
    let zoomLevel = this.matrix[0][0];

    if (Math.abs(zoomLevel - 1.0) < 0.01) {
      zoomLevel = 1.0;
    }

    return zoomLevel;
  }

  clearMarking(): void {
    if (this.markedFeature) {
      const polygon = this.polygonElement().nativeElement;
      polygon.setAttribute('visibility', 'hidden');
      this.markedFeature = undefined;
    }
  }

  setMarking(clickableFeature: T | undefined): void {
    if (clickableFeature) {
      let pointsString = '';

      if (clickableFeature.points?.length) {
        for (let i = 0; i < clickableFeature.points.length; i++) {
          const point = this.adjustPointForInitialScaling(clickableFeature.points[i].x, clickableFeature.points[i].y);
          pointsString += `${point[0]},${point[1]} `;
        }
      } else {
        const upperLeft = this.adjustPointForInitialScaling(clickableFeature.left, clickableFeature.top);
        const lowerRight = this.adjustPointForInitialScaling(clickableFeature.right, clickableFeature.bottom);
        pointsString = `${upperLeft[0]},${upperLeft[1]} ${lowerRight[0]},${upperLeft[1]} ${lowerRight[0]},${lowerRight[1]} ${upperLeft[0]},${lowerRight[1]}`;
      }

      const polygon = this.polygonElement().nativeElement;
      polygon.setAttribute('points', pointsString.trim());
      polygon.setAttribute('visibility', 'visible');
      this.markedFeature = clickableFeature;
    } else {
      this.clearMarking();
    }
  }

  resetImage(): void {
    if (this.imageDataWidth > 0 && this.imageDataHeight > 0) {
      this.resetImageCore(this.markedFeature, this.imageDataWidth, this.imageDataHeight);
    }
  }

  private svgElement = viewChild.required<ElementRef<SVGSVGElement>>('svg');
  private gElement = viewChild.required<ElementRef<SVGGElement>>('g');
  private imageElement = viewChild.required<ElementRef<SVGImageElement>>('image');
  private polygonElement = viewChild.required<ElementRef<SVGPolygonElement>>('polygon');

  private initialTransformation: number[][] = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  private matrix: number[][] = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  private zoomRatioCount = 0;
  private imageDataWidth = 0;
  private imageDataHeight = 0;
  private panAnchor?: { x: number; y: number };
  private panStartCoords?: { x: number; y: number };
  private isPanning = false;
  private markedFeature?: T;
  private overlappingBoundingBoxes: { feature: T; area: number }[][] = [];

  private lastTouchDistance?: number;
  private lastTouchCenter?: { x: number; y: number };
  private hasPannedOrZoomed = false;

  constructor() {
    effect(() => {
      this.overlappingBoundingBoxes.length = 0;
      this.overlappingBoundingBoxes = [];
      const clickableFeatures = this.clickableFeatures();

      if (clickableFeatures.length > 0) {
        for (const currentFeature of clickableFeatures) {
          const overlappingFeatures: { feature: T; area: number }[] = [];
          overlappingFeatures.push({ feature: currentFeature, area: this.getArea(currentFeature) });

          for (const otherFeature of clickableFeatures) {
            if (
              otherFeature !== currentFeature &&
              (this.isBoundingBoxOverlap(otherFeature, currentFeature) ||
                this.isBoundingBoxOverlap(currentFeature, otherFeature))
            ) {
              overlappingFeatures.push({ feature: otherFeature, area: this.getArea(otherFeature) });
            }
          }

          this.overlappingBoundingBoxes.push(overlappingFeatures);
        }
      }
    });
    effect(() => {
      const imageData = this.image();

      if (!imageData.imageBase64 || imageData.width <= 0 || imageData.height <= 0) {
        return;
      }

      this.resetImageCore(imageData.initialMarking, imageData.width, imageData.height, imageData.imageBase64);
    });
    effect(() => {
      const size = this.size();

      if (size.width > 0 && size.height > 0) {
        const svgElement = this.svgElement().nativeElement;
        const oldWidth = svgElement.getAttribute('width');
        const oldHeight = svgElement.getAttribute('height');
        const newWidth = size.width.toString();
        const newHeight = size.height.toString();
        const widthChanged = newWidth !== oldWidth;
        const heightChanged = newHeight !== oldHeight;

        if (widthChanged) {
          svgElement.setAttribute('width', newWidth);
        }

        if (heightChanged) {
          svgElement.setAttribute('height', newHeight);
        }

        if ((widthChanged || heightChanged) && size.resetImage) {
          this.resetImage();
        }
      }
    });
  }

  ngOnInit() {
    const svgElement = this.svgElement().nativeElement;
    svgElement.addEventListener('mousedown', (e: MouseEvent) => this.setAnchor(e));
    svgElement.addEventListener('mousemove', (e: MouseEvent) => this.pan(e));
    svgElement.addEventListener('mouseup', (e: MouseEvent) => this.handleMouseUp(e));
    svgElement.addEventListener('mouseleave', (e: MouseEvent) => this.clearAnchor(e));
    svgElement.addEventListener('wheel', (e: WheelEvent) => this.zoom(e));

    svgElement.addEventListener('touchstart', (e: TouchEvent) => this.handleTouchStart(e), { passive: false });
    svgElement.addEventListener('touchmove', (e: TouchEvent) => this.handleTouchMove(e), { passive: false });
    svgElement.addEventListener('touchend', (e: TouchEvent) => this.handleTouchEnd(e));
    svgElement.addEventListener('touchcancel', (e: TouchEvent) => this.handleTouchEnd(e));
  }

  /**
   * Handle the start of a touch gesture on the SVG.
   *
   * Prevents gesture conflicts with parent components:
   * - Stops propagation if zoomed in or starting a multi-touch gesture.
   * - Bubbles single-finger touches at 1.0 zoom to allow tab swiping.
   */
  private handleTouchStart(e: TouchEvent): void {
    const isMultiTouch = e.touches.length > 1;
    if (isMultiTouch) {
      e.stopImmediatePropagation();
    }

    this.hasPannedOrZoomed = false;
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      this.panAnchor = this.getTouchCoordinates(touch);
      this.panStartCoords = { ...this.panAnchor };
      this.isPanning = true;
    } else if (e.touches.length === 2) {
      this.isPanning = false;
      this.panAnchor = undefined;
      this.panStartCoords = undefined;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const p1 = this.getTouchCoordinates(touch1);
      const p2 = this.getTouchCoordinates(touch2);

      this.lastTouchDistance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      this.lastTouchCenter = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
    }
  }

  /**
   * Handle touch movement.
   *
   * Distinguishes between panning (internal image movement) and swiping (tab switching)
   * by checking image boundaries and gesture distance.
   */
  private handleTouchMove(e: TouchEvent): void {
    const isZoomed = this.zoomLevel !== 1.0;
    const isMultiTouch = e.touches.length > 1;

    if (isMultiTouch) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }

    if (e.touches.length === 1 && this.isPanning && this.panAnchor && this.panStartCoords) {
      const touch = e.touches[0];
      const coords = this.getTouchCoordinates(touch);
      const dx = coords.x - this.panStartCoords.x;
      const dy = coords.y - this.panStartCoords.y;
      const distance = Math.hypot(dx, dy);

      if (isZoomed) {
        const svgWidth = this.svgElement().nativeElement.clientWidth;
        const translateX = this.matrix[0][2];
        const scaleX = this.matrix[0][0];

        const epsilon = 5.0;
        const isAtLeftEdge = translateX >= -epsilon;
        const isAtRightEdge = translateX + svgWidth * scaleX <= svgWidth + epsilon;
        const isHorizontalMove = Math.abs(dx) > Math.abs(dy) * 1.5;

        const isSwipingOut = (isAtLeftEdge && dx > 0) || (isAtRightEdge && dx < 0);

        if (isHorizontalMove && isSwipingOut) {
          if (distance < 20) {
            e.stopImmediatePropagation();
            return;
          }
          this.hasPannedOrZoomed = false;
          return;
        }

        e.stopImmediatePropagation();
        e.preventDefault();

        if (!this.hasPannedOrZoomed && distance > 5) {
          this.hasPannedOrZoomed = true;
        }

        const offset_translation = [
          [1, 0, coords.x - this.panAnchor.x],
          [0, 1, coords.y - this.panAnchor.y],
          [0, 0, 1],
        ];

        const matrix = this.matrixMatrixMult(offset_translation, this.matrix);
        this.applyTransform(matrix);
        this.matrix = matrix;
        this.panAnchor = coords;
      } else if (!isMultiTouch) {
        if (distance > 10) {
          const isHorizontal = Math.abs(dx) > Math.abs(dy) * 1.5;
          if (!isHorizontal) {
            e.stopImmediatePropagation();
            e.preventDefault();
            this.hasPannedOrZoomed = true;
          } else {
            this.hasPannedOrZoomed = false;
          }
        }
      }
    } else if (isMultiTouch && this.lastTouchDistance && this.lastTouchCenter) {
      e.preventDefault();
      this.hasPannedOrZoomed = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const p1 = this.getTouchCoordinates(touch1);
      const p2 = this.getTouchCoordinates(touch2);

      const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const center = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };

      if (this.lastTouchDistance > 0) {
        const zoomRatio = distance / this.lastTouchDistance;
        this.applyZoomAt(zoomRatio, center.x, center.y);
      }

      this.lastTouchDistance = distance;
      this.lastTouchCenter = center;
    }
  }

  /**
   * Handle the end of a touch gesture.
   */
  private handleTouchEnd(e: TouchEvent): void {
    if (this.hasPannedOrZoomed) {
      e.stopImmediatePropagation();
    }

    if (e.touches.length === 0) {
      if (!this.hasPannedOrZoomed && this.panAnchor) {
        this.select(this.panAnchor.x, this.panAnchor.y);
      }
      this.isPanning = false;
      this.panAnchor = undefined;
      this.panStartCoords = undefined;
      this.lastTouchDistance = undefined;
      this.lastTouchCenter = undefined;
      this.hasPannedOrZoomed = false;
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      this.panAnchor = this.getTouchCoordinates(touch);
      this.panStartCoords = { ...this.panAnchor };
      this.isPanning = true;
      this.lastTouchDistance = undefined;
      this.lastTouchCenter = undefined;
    }
  }

  private getTouchCoordinates(touch: Touch): { x: number; y: number } {
    const rect = this.svgElement().nativeElement.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }

  private applyZoomAt(zoomRatio: number, centerX: number, centerY: number): void {
    const positive_center_translation = [
      [1, 0, centerX],
      [0, 1, centerY],
      [0, 0, 1],
    ];
    const negative_center_translation = [
      [1, 0, -centerX],
      [0, 1, -centerY],
      [0, 0, 1],
    ];
    const zoom_scaling = [
      [zoomRatio, 0, 0],
      [0, zoomRatio, 0],
      [0, 0, 1],
    ];

    let matrix = this.matrixMatrixMult(negative_center_translation, this.matrix);
    matrix = this.matrixMatrixMult(zoom_scaling, matrix);
    this.matrix = this.matrixMatrixMult(positive_center_translation, matrix);

    this.applyTransform(this.matrix);
  }

  private resetImageCore(
    featureToMark: T,
    imageDataWidth: number,
    imageDataHeight: number,
    imageBase64?: string,
  ): void {
    const rect = this.svgElement().nativeElement.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    if (width <= 0 || height <= 0) {
      return;
    }

    this.imageDataWidth = imageDataWidth;
    this.imageDataHeight = imageDataHeight;
    const imageElement = this.imageElement().nativeElement;

    if (imageBase64) {
      imageElement.setAttribute('href', imageBase64);
    }

    imageElement.setAttribute('x', '0');
    imageElement.setAttribute('y', '0');
    imageElement.setAttribute('width', width.toString());
    imageElement.setAttribute('height', height.toString());
    this.zoomRatioCount = 0;
    this.matrix = [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ];
    this.applyTransform(this.matrix);
    this.prepareInitialScaling(imageDataWidth, imageDataHeight, width, height);
    this.setMarking(featureToMark);
  }

  private handleMouseUp(e: MouseEvent): void {
    if (e.button === 0 && (e.buttons === 0 || e.buttons === 1) && !this.isPanning) {
      const coords = this.getCoordinates(e);
      this.select(coords.x, coords.y);
    }

    this.clearAnchor(e);
  }

  private setAnchor(e: MouseEvent): void {
    this.panAnchor = this.getCoordinates(e);

    if (e.button === 1 && (e.buttons === 0 || e.buttons === 4)) {
      this.isPanning = true;
    }
  }

  private clearAnchor(e: MouseEvent): void {
    if (this.isPanning) {
      this.pan(e, true);
    }

    this.isPanning = false;
    this.panAnchor = undefined;
  }

  private pan(e: MouseEvent, persist: boolean = false): void {
    if (!this.isPanning) {
      return;
    }

    e.preventDefault();
    const coords = this.getCoordinates(e);

    const offset_translation = [
      [1, 0, coords.x - this.panAnchor!.x],
      [0, 1, coords.y - this.panAnchor!.y],
      [0, 0, 1],
    ];

    const matrix = this.matrixMatrixMult(offset_translation, this.matrix);
    this.applyTransform(matrix);

    if (persist) {
      this.matrix = matrix;
    }
  }

  private zoom(e: WheelEvent): void {
    if (this.panAnchor) {
      return;
    }

    let zoomRatio: number;

    if (e.deltaY > 0) {
      if (this.zoomRatioCount-- > 0) {
        zoomRatio = 1.0 / this._ZoomIncrementValue;
      } else {
        zoomRatio = this._ZoomDecrementValue;
      }
    } else if (e.deltaY < 0) {
      if (this.zoomRatioCount++ < 0) {
        zoomRatio = 1.0 / this._ZoomDecrementValue;
      } else {
        zoomRatio = this._ZoomIncrementValue;
      }
    } else {
      return;
    }

    e.preventDefault();
    const coords = this.getCoordinates(e);
    this.applyZoomAt(zoomRatio, coords.x, coords.y);
  }

  private select(x: number, y: number): void {
    if (this.isInSelectTolerance(x, y)) {
      const polygon = this.polygonElement().nativeElement;
      const feature = this.match(x, y, polygon);
      this.onFeatureClicked.emit(feature);
      polygon.setAttribute('visibility', feature ? 'visible' : 'hidden');
      this.markedFeature = feature;
    }
  }

  private isInSelectTolerance(x: number, y: number): boolean {
    return !this.panAnchor || (Math.abs(this.panAnchor.x - x) < 5 && Math.abs(this.panAnchor.y - y) < 5);
  }

  private match(x: number, y: number, polygon: SVGPolygonElement): T | undefined {
    if (this.overlappingBoundingBoxes.length > 0) {
      const matrixInverted = this.invertMatrix(this.matrix);
      const vector = this.matrixVectorMult(matrixInverted, [x, y, 1]);
      const bestMatch = this.findBestMatch(vector);

      if (bestMatch) {
        polygon.setAttribute('points', bestMatch.pointsString.trim());
        return bestMatch.feature;
      }
    }

    return undefined;
  }

  private findBestMatch(vector: number[]): { feature: T; pointsString: string } | undefined {
    let candidates: { feature: T; area: number; pointsString: string }[] = <any>this.findMatchCandidates(vector).filter(
      (candidate) => {
        const pointsString = [''];

        if (this.matchPolygon(vector, candidate.feature.points, pointsString)) {
          (<any>candidate).pointsString = pointsString[0];
          return true;
        }

        return false;
      },
    );

    if (candidates.length === 0) {
      return undefined;
    }

    if (candidates.length > 1) {
      candidates.sort(
        (feature1: { feature: T; area: number }, feature2: { feature: T; area: number }) =>
          feature1.area - feature2.area,
      );

      const minArea = candidates[0].area;
      const minAreaCutoff = 0.5 * minArea;
      candidates = candidates.filter((candidate) => Math.abs(candidate.area - minArea) < minAreaCutoff);
    }

    if (candidates.length === 1 && candidates[0].pointsString) {
      return { feature: candidates[0].feature, pointsString: candidates[0].pointsString };
    }

    let bestMatch: { feature: T; pointsString: string } | undefined = undefined;
    let lowestDistance: number | null = null;

    for (const candidate of candidates) {
      const upperLeft = this.adjustPointForInitialScaling(candidate.feature.left, candidate.feature.top);
      const lowerRight = this.adjustPointForInitialScaling(candidate.feature.right, candidate.feature.bottom);
      const center = [
        upperLeft[0] + (lowerRight[0] - upperLeft[0]) / 2,
        lowerRight[1] + (upperLeft[1] - lowerRight[1]) / 2,
      ];
      const distance = Math.sqrt(Math.pow(vector[0] - center[0], 2) + Math.pow(vector[1] - center[1], 2));

      if (lowestDistance === null || distance < lowestDistance) {
        lowestDistance = distance;
        bestMatch = { feature: candidate.feature, pointsString: candidate.pointsString };

        if (!bestMatch.pointsString) {
          bestMatch.pointsString = `${upperLeft[0]},${upperLeft[1]} ${lowerRight[0]},${upperLeft[1]} ${lowerRight[0]},${lowerRight[1]} ${upperLeft[0]},${lowerRight[1]}`;
        }
      }
    }

    return bestMatch;
  }

  private findMatchCandidates(vector: number[]): { feature: T; area: number }[] {
    const candidates = this.overlappingBoundingBoxes.find((overlappingFeatures) =>
      this.matchBoundingBox(vector, overlappingFeatures[0].feature),
    );

    if (candidates) {
      return candidates.length === 1
        ? candidates
        : candidates.filter((featureAndArea) => this.matchBoundingBox(vector, featureAndArea.feature));
    }

    return [];
  }

  private matchBoundingBox(vector: number[], feature: T): boolean {
    const upperLeft = this.adjustPointForInitialScaling(feature.left, feature.top);
    const lowerRight = this.adjustPointForInitialScaling(feature.right, feature.bottom);

    return (
      upperLeft[0] <= vector[0] && vector[0] <= lowerRight[0] && upperLeft[1] <= vector[1] && vector[1] <= lowerRight[1]
    );
  }

  private isBoundingBoxOverlap(feature1: T, feature2: T): boolean {
    const upperLeft = [feature1.left, feature1.top];
    const upperRight = [feature1.right, feature1.top];
    const lowerLeft = [feature1.left, feature1.bottom];
    const lowerRight = [feature1.right, feature1.bottom];
    const corners = [upperLeft, upperRight, lowerLeft, lowerRight];

    for (const corner of corners) {
      if (
        corner[0] >= feature2.left &&
        corner[0] <= feature2.right &&
        corner[1] >= feature2.top &&
        corner[1] <= feature2.bottom
      ) {
        return true;
      }
    }

    return false;
  }

  private getArea(feature: T): number {
    return Math.abs((feature.right - feature.left) * (feature.top - feature.bottom));
  }

  private matchPolygon(
    vector: number[],
    points: { x: number; y: number }[] | undefined,
    pointsString: string[],
  ): boolean {
    if (!points?.length) {
      return true;
    }

    let isInside = false;

    for (let i = 0; i < points.length; i++) {
      const _point = points[i == 0 ? points.length - 1 : i - 1];
      const _nextPoint = points[i];
      const point = this.adjustPointForInitialScaling(_point.x, _point.y);
      const nextPoint = this.adjustPointForInitialScaling(_nextPoint.x, _nextPoint.y);
      pointsString[0] = pointsString[0] + `${nextPoint[0]},${nextPoint[1]} `;

      if (point[1] > vector[1] != nextPoint[1] > vector[1]) {
        const x_intersection =
          point[0] + ((vector[1] - point[1]) * (nextPoint[0] - point[0])) / (nextPoint[1] - point[1]);

        if (x_intersection > vector[0]) {
          isInside = !isInside;
        }
      }
    }

    if (!isInside) {
      pointsString[0] = '';
    }

    return isInside;
  }

  private adjustPointForInitialScaling(x: number, y: number): number[] {
    return this.matrixVectorMult(this.initialTransformation, [x, y, 1]);
  }

  private prepareInitialScaling(
    rasterImageWidth: number,
    rasterImageHeight: number,
    svgImageElementWidth: number,
    svgImageElementHeight: number,
  ): void {
    const initialCenterX = rasterImageWidth / 2;
    const initialCenterY = rasterImageHeight / 2;
    const initialCutoffX = (rasterImageWidth - svgImageElementWidth) / 2;
    const initialCutoffY = (rasterImageHeight - svgImageElementHeight) / 2;
    let initialScaling = svgImageElementWidth / rasterImageWidth;

    if (initialScaling * rasterImageHeight > svgImageElementHeight) {
      initialScaling = svgImageElementHeight / rasterImageHeight;
    }

    const positive_center_translation = [
      [1, 0, initialCenterX],
      [0, 1, initialCenterY],
      [0, 0, 1],
    ];
    const negative_center_translation = [
      [1, 0, -initialCenterX],
      [0, 1, -initialCenterY],
      [0, 0, 1],
    ];
    const zoom_scaling = [
      [initialScaling, 0, 0],
      [0, initialScaling, 0],
      [0, 0, 1],
    ];
    const origin_cutoff_translation = [
      [1, 0, -initialCutoffX],
      [0, 1, -initialCutoffY],
      [0, 0, 1],
    ];

    let matrix = this.matrixMatrixMult(zoom_scaling, negative_center_translation);
    matrix = this.matrixMatrixMult(positive_center_translation, matrix);
    this.initialTransformation = this.matrixMatrixMult(origin_cutoff_translation, matrix);
  }

  private getCoordinates(e: MouseEvent): { x: number; y: number } {
    return { x: e.offsetX, y: e.offsetY };
  }

  private applyTransform(matrix: number[][]): void {
    this.gElement().nativeElement.setAttribute('transform', this.toTransformString(matrix));
  }

  private toTransformString(matrix: number[][]): string {
    const transform = `matrix(${matrix[0][0]} 0 0 ${matrix[1][1]} ${matrix[0][2]} ${matrix[1][2]})`;
    return transform;
  }

  private matrixVectorMult(matrix: number[][], vector: number[]): number[] {
    const result = [
      matrix[0][0] * vector[0] + matrix[0][1] * vector[1] + matrix[0][2] * vector[2],
      matrix[1][0] * vector[0] + matrix[1][1] * vector[1] + matrix[1][2] * vector[2],
      matrix[2][0] * vector[0] + matrix[2][1] * vector[1] + matrix[2][2] * vector[2],
    ];

    return result;
  }

  private matrixMatrixMult(matrix1: number[][], matrix2: number[][]): number[][] {
    const matrix: number[][] = [];

    matrix[0] = [
      matrix1[0][0] * matrix2[0][0] + matrix1[0][1] * matrix2[1][0] + matrix1[0][2] * matrix2[2][0],
      matrix1[0][0] * matrix2[0][1] + matrix1[0][1] * matrix2[1][1] + matrix1[0][2] * matrix2[2][1],
      matrix1[0][0] * matrix2[0][2] + matrix1[0][1] * matrix2[1][2] + matrix1[0][2] * matrix2[2][2],
    ];
    matrix[1] = [
      matrix1[1][0] * matrix2[0][0] + matrix1[1][1] * matrix2[1][0] + matrix1[1][2] * matrix2[2][0],
      matrix1[1][0] * matrix2[0][1] + matrix1[1][1] * matrix2[1][1] + matrix1[1][2] * matrix2[2][1],
      matrix1[1][0] * matrix2[0][2] + matrix1[1][1] * matrix2[1][2] + matrix1[1][2] * matrix2[2][2],
    ];
    matrix[2] = [
      matrix1[2][0] * matrix2[0][0] + matrix1[2][1] * matrix2[1][0] + matrix1[2][2] * matrix2[2][0],
      matrix1[2][0] * matrix2[0][1] + matrix1[2][1] * matrix2[1][1] + matrix1[2][2] * matrix2[2][1],
      matrix1[2][0] * matrix2[0][2] + matrix1[2][1] * matrix2[1][2] + matrix1[2][2] * matrix2[2][2],
    ];

    return matrix;
  }

  private invertMatrix(matrix: number[][]): number[][] {
    const matrixInverted: number[][] = [];

    const determinantInverted =
      1 /
      (matrix[0][0] * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
        matrix[0][1] * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
        matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0]));

    matrixInverted[0] = [
      determinantInverted * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]),
      -determinantInverted * (matrix[0][1] * matrix[2][2] - matrix[0][2] * matrix[2][1]),
      determinantInverted * (matrix[0][1] * matrix[1][2] - matrix[0][2] * matrix[1][1]),
    ];
    matrixInverted[1] = [
      -determinantInverted * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]),
      determinantInverted * (matrix[0][0] * matrix[2][2] - matrix[0][2] * matrix[2][0]),
      -determinantInverted * (matrix[0][0] * matrix[1][2] - matrix[0][2] * matrix[1][0]),
    ];
    matrixInverted[2] = [
      determinantInverted * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0]),
      -determinantInverted * (matrix[0][0] * matrix[2][1] - matrix[0][1] * matrix[2][0]),
      determinantInverted * (matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0]),
    ];

    for (let row = 0; row < 3; row++) {
      for (let column = 0; column < 3; column++) {
        if (Math.abs(matrixInverted[row][column]) === 0) {
          matrixInverted[row][column] = 0;
        }
      }
    }

    return matrixInverted;
  }
}
